import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon.tsx";

type IconName = Parameters<typeof Icon>[0]["name"];

/**
 * A small button opening a panel under it (settings, help): closes on Escape, a click
 * outside, or its own close action; the focus goes back to the button.
 */
export function Popover({
  icon,
  label,
  showLabel = false,
  align = "end",
  above = false,
  className = "",
  children,
}: {
  icon: IconName;
  label: string;
  /** The label as text beside the icon (else only for screen readers and the tooltip). */
  showLabel?: boolean;
  align?: "start" | "end";
  /** Opens over the button (one at the bottom of the screen). */
  above?: boolean;
  className?: string;
  children: ReactNode | ((close: () => void) => ReactNode);
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key, true);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key, true);
    };
  }, [open]);
  return (
    <div className={`popover ${className}`} ref={box}>
      <button
        ref={button}
        type="button"
        className={`btn btn-quiet btn-small popover-button${showLabel ? "" : " is-icon"}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={id}
        aria-label={showLabel ? undefined : label}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name={icon} />
        {showLabel && <span>{label}</span>}
      </button>
      {open && (
        <div id={id} role="dialog" aria-label={label} className={`popover-panel is-${align}${above ? " is-above" : ""}`}>
          {typeof children === "function" ? children(close) : children}
        </div>
      )}
    </div>
  );
}
